import {useNavigate, useParams} from "react-router-dom";
import React, {useEffect, useMemo, useRef, useState} from "react";
import socket from "../utils/clientSocket";
import {fetchFriendMessages, fetchFriends} from "../clients/FriendClient";
import {useUser} from "../UserContext";
import UnauthorizedMessage from "../components/UnauthorizedMessage";
import Avatar from "../components/ui/Avatar";
import Icon from "../components/ui/Icon";
import CreateGameModal from "../components/CreateGameModal";
import {clearUnread, useNotifications} from "../utils/notificationStore";
import usePageTheme from "../utils/usePageTheme";

const dayLabel = (date) => {
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    if (date.toDateString() === today.toDateString()) return "Today";
    if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
    return date.toLocaleDateString("en-GB", {weekday: "long", day: "numeric", month: "long"});
};

// Dates arrive as strings from the server - guard against anything unparsable
const toDate = (value) => {
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

const Conversation = ({friendName, friend, onBack}) => {
    const user = useUser();
    const navigate = useNavigate();
    const [messages, setMessages] = useState(null)
    const [chatInput, setChatInput] = useState("")
    const [errorMessage, setErrorMessage] = useState(null)
    const [showChallenge, setShowChallenge] = useState(false)
    const bodyRef = useRef(null)
    const inputRef = useRef(null)

    useEffect(() => {
        setMessages(null);
        setErrorMessage(null);
        clearUnread(friendName);
        fetchFriendMessages(friendName, 1)
            .then((data) => setMessages(data.map((m) => ({...m, timeSent: toDate(m.timeSent)}))))
            .catch((error) => {
                setMessages([]);
                setErrorMessage(error.response?.data?.message || error.message);
            });

        const handleMessage = (message) => {
            // Only show messages from the friend we are talking to - others become notifications
            if (message.senderName !== friendName) return;
            setMessages((prev) => [...(prev || []), {...message, timeSent: toDate(message.timeSent) || new Date()}]);
            clearUnread(friendName);
        };
        socket.on("friendMessage", handleMessage);
        inputRef.current?.focus();
        return () => socket.off("friendMessage", handleMessage);
    }, [friendName]);

    useEffect(() => {
        const body = bodyRef.current;
        if (body) body.scrollTop = body.scrollHeight;
    }, [messages]);

    const sendMessage = (event) => {
        event?.preventDefault();
        const text = chatInput.trim();
        if (!text) return;
        const newMsg = {message: text, friendName, timeSent: new Date()};
        socket.emit('friendMessage', newMsg);
        setMessages((prev) => [...(prev || []), {...newMsg, senderName: user.username}]);
        setChatInput("");
        inputRef.current?.focus();
    };

    // Group the messages by day, and mark messages that follow one from the same sender
    const items = useMemo(() => {
        const result = [];
        let lastDay = null;
        const timeOf = (msg) => (msg?.timeSent ? msg.timeSent.getTime() : 0);
        (messages || []).forEach((msg, index) => {
            const day = msg.timeSent ? msg.timeSent.toDateString() : lastDay;
            if (msg.timeSent && day !== lastDay) {
                result.push({type: "day", key: `day-${day}-${index}`, label: dayLabel(msg.timeSent)});
                lastDay = day;
            }
            const next = messages[index + 1];
            const showTime = !!msg.timeSent && (!next || next.senderName !== msg.senderName
                || timeOf(next) - timeOf(msg) > 5 * 60 * 1000 || (next.timeSent && next.timeSent.toDateString() !== day));
            const prev = messages[index - 1];
            const grouped = prev && prev.senderName === msg.senderName && timeOf(msg) - timeOf(prev) < 5 * 60 * 1000;
            result.push({type: "msg", key: `m-${index}`, msg, showTime, grouped});
        });
        return result;
    }, [messages]);

    return (
        <section className="conversation">
            <div className="conversation__header">
                <button className="icon-btn back-btn" onClick={onBack} aria-label="Back to conversations">
                    <Icon name="arrowLeft"/>
                </button>
                <Avatar name={friendName} size={40} online={friend ? !!friend.isActive : undefined}/>
                <div style={{flex: 1, minWidth: 0}}>
                    <button className="user-link user-link--clickable" style={{fontSize: "1.05rem"}}
                            onClick={() => navigate(`/profile?username=${encodeURIComponent(friendName)}`)}>
                        {friendName}
                    </button>
                    <div className="small muted" style={{fontWeight: 700}}>
                        {friend ? (friend.isActive ? "Online now" : "Offline") : ""}
                    </div>
                </div>
                <button className="btn btn-sm btn-secondary" onClick={() => setShowChallenge(true)}
                        disabled={friend && !friend.isActive} title={friend && !friend.isActive ? "Your friend is offline" : "Challenge to a game"}>
                    <Icon name="swords"/> Challenge
                </button>
                <CloseMessagesButton/>
            </div>

            <div className="conversation__body" ref={bodyRef}>
                {messages === null && <span className="spinner" style={{margin: "auto"}}/>}
                {errorMessage && <div className="form-error">{errorMessage}</div>}
                {messages && messages.length === 0 && !errorMessage && (
                    <div className="empty-state" style={{margin: "auto"}}>
                        <Avatar name={friendName} size={56}/>
                        <strong>This is the start of your conversation with {friendName}</strong>
                        <span className="small">Say hi, or challenge them to a game.</span>
                    </div>
                )}
                {items.map((item) => item.type === "day" ? (
                    <div className="day-sep" key={item.key}>{item.label}</div>
                ) : (
                    <div key={item.key}
                         className={`bubble-row ${item.msg.senderName === user.username ? "bubble-row--self" : ""} ${item.grouped ? "bubble-row--grouped" : ""}`}>
                        <div className="bubble">{item.msg.message}</div>
                        {item.showTime && (
                            <div className="bubble__time">
                                {item.msg.timeSent.toLocaleTimeString("en-GB", {hour: "2-digit", minute: "2-digit"})}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            <form className="conversation__composer" onSubmit={sendMessage}>
                <textarea
                    ref={inputRef}
                    className="textarea"
                    rows={1}
                    value={chatInput}
                    maxLength={1000}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            sendMessage();
                        }
                    }}
                    placeholder={`Message ${friendName}…`}
                    aria-label={`Message ${friendName}`}
                />
                <button type="submit" className="icon-btn" disabled={!chatInput.trim()} aria-label="Send">
                    <Icon name="send"/>
                </button>
            </form>
            <CreateGameModal showCreateModal={showChallenge} closeModal={() => setShowChallenge(false)} friendName={friendName}/>
        </section>
    );
};

// Closes the messages box and goes back to your profile, where the friends list is
const CloseMessagesButton = () => {
    const navigate = useNavigate();
    return (
        <button type="button" className="icon-btn" onClick={() => navigate("/my-profile")}
                aria-label="Close messages" title="Close">
            <Icon name="close"/>
        </button>
    );
};

const FriendChat = () => {
    usePageTheme("lake");
    const {friendName} = useParams()
    const navigate = useNavigate();
    const user = useUser();
    const {unreadByFriend} = useNotifications();
    const [friends, setFriends] = useState(null);
    const [filter, setFilter] = useState("");

    useEffect(() => {
        if (!user.isLoggedIn) return;
        const load = () => fetchFriends()
            .then((data) => setFriends([...data].sort((a, b) => Number(b.isActive) - Number(a.isActive) || a.friendName.localeCompare(b.friendName))))
            .catch(() => setFriends([]));
        load();
        const interval = setInterval(load, 30000);
        return () => clearInterval(interval);
    }, [user.isLoggedIn]);

    if (!user.loaded) {
        return <div className="page" style={{display: "flex", justifyContent: "center", paddingTop: "4rem"}}><span className="spinner"/></div>;
    }

    if (!user.isLoggedIn) {
        return <div className="page page--narrow"><UnauthorizedMessage/></div>;
    }

    const visibleFriends = (friends || []).filter((f) => f.friendName.toLowerCase().includes(filter.trim().toLowerCase()));
    const activeFriend = friends?.find((f) => f.friendName === friendName);

    return (
        <div className="page">
            <div className={`card messages-layout ${friendName ? "has-active" : ""}`}>
                <aside className="conv-list">
                    <div className="card-header" style={{flexDirection: "column", alignItems: "stretch"}}>
                        <div className="card-title"><Icon name="chat"/> Messages</div>
                        <input className="input" placeholder="Search friends" value={filter}
                               onChange={(e) => setFilter(e.target.value)} aria-label="Search friends"
                               style={{padding: "0.5rem 0.75rem", fontSize: "0.9rem"}}/>
                    </div>
                    <div className="conv-list__items">
                        {friends === null && <div className="card-body"><span className="spinner"/></div>}
                        {friends && friends.length === 0 && (
                            <div className="empty-state">
                                <Icon name="users"/>
                                <strong>No friends yet</strong>
                                <span className="small">Add friends from your profile to start chatting.</span>
                                <button className="btn btn-sm btn-secondary" onClick={() => navigate("/my-profile")}>Go to profile</button>
                            </div>
                        )}
                        {visibleFriends.map((friend) => {
                            const unread = unreadByFriend[friend.friendName] || 0;
                            return (
                                <button key={friend.friendName} type="button"
                                        className={`conv-item ${friend.friendName === friendName ? "active" : ""}`}
                                        onClick={() => navigate(`/messages/${encodeURIComponent(friend.friendName)}`)}>
                                    <Avatar name={friend.friendName} size={38} online={!!friend.isActive}/>
                                    <div className="conv-item__info">
                                        <div className="conv-item__name">{friend.friendName}</div>
                                        <div className="conv-item__sub">{unread > 0 ? `${unread} new ${unread === 1 ? "message" : "messages"}` : friend.isActive ? "Online" : "Offline"}</div>
                                    </div>
                                    {unread > 0 && <span className="nav-link__badge">{unread}</span>}
                                </button>
                            );
                        })}
                    </div>
                </aside>
                {friendName ? (
                    <Conversation key={friendName} friendName={friendName} friend={activeFriend}
                                  onBack={() => navigate("/messages")}/>
                ) : (
                    <section className="conversation">
                        <div className="conversation__close-row">
                            <CloseMessagesButton/>
                        </div>
                        <div className="empty-state" style={{margin: "auto"}}>
                            <Icon name="chat"/>
                            <strong>Pick a friend to start chatting</strong>
                            <span className="small">Your conversations are saved, so you can pick up where you left off.</span>
                        </div>
                    </section>
                )}
            </div>
        </div>
    )
}

export default FriendChat;
